import { SelectCompanyRole } from "@components/role/select-company-role";
import { useIsXgSeatRequiredForRole } from "@components/table/members/assign-seats/use-is-xg-seat-required-for-role";
import { XgSeatRequiredForRoleDialog } from "@components/table/members/assign-seats/xg-seat-required-for-role-dialog";
import { MemberRoleReadOnly } from "@components/table/members/members-column/role-column/member-role-read-only";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { useState } from "react";
import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { Stack } from "@mui-wrapper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { formatUserRoleType } from "@utils/data-display";
import { getAssignableWorkspaceRoles } from "@utils/workspace-role-utils";
import { Text } from "@flat-ui";
/**
 * Render and adjust member roles in company context.
 * This dropdown is showed to change the role for a single member inside a table.
 */
export function CompanyMemberRole({
  companyId,
  memberIdentityRole,
  isReadOnly = false,
  variant,
  menuProps,
  location,
  teamName,
  member
}) {
  const {
    trackEvent
  } = useTrackEvent();
  const currentUser = useAppSelector(currentUserSelector);
  const {
    canEditMember
  } = useHasUserValidRoleCompanyLevel();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    createDialog
  } = useSphereDialog();
  const isXgSeatRequiredForRole = useIsXgSeatRequiredForRole(member);
  const [isUpdating, setIsUpdating] = useState(false);

  // The role picked for a member that needs a Sphere XG seat for it first
  const [roleAwaitingXgSeat, setRoleAwaitingXgSeat] = useState();
  const isCurrentUser = currentUser?.identity === memberIdentityRole.identity;
  const {
    changeMemberRole
  } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "workspace",
    memberIdentity: memberIdentityRole.identity,
    currentRole: memberIdentityRole.role
  });
  if (isReadOnly) {
    return <MemberRoleReadOnly role={memberIdentityRole.role} variant={variant} />;
  }

  /**
   * Return non-editable member role if:
   * - Member is current logged user
   * - User has no permission to edit member
   */
  if (isCurrentUser || !canEditMember) {
    return <MemberRoleReadOnly role={memberIdentityRole.role} variant={variant} />;
  }

  /** Trigger updating member role */
  async function onRoleChange(role) {
    trackEvent({
      name: WorkspaceMemberEvents.changeRole,
      props: {
        role
      }
    });
    if (isUpdating) {
      return;
    }

    // Changing a team's role affects every member of the team, so confirm first.
    if (teamName && !(await confirmTeamRoleChange(role))) {
      return;
    }

    // The role needs a Sphere XG seat, which the dialog assigns before it changes the role
    if (isXgSeatRequiredForRole(role)) {
      trackEvent({
        name: WorkspaceMemberEvents.openXgSeatRequiredForRole,
        props: {
          role,
          location
        }
      });
      setRoleAwaitingXgSeat(role);
      return;
    }
    await applyRoleChange(role);
  }

  /** Changes the role of the member, showing a spinner next to the dropdown meanwhile */
  async function applyRoleChange(role_0) {
    try {
      setIsUpdating(true);
      await changeMemberRole(role_0);
    } catch {
      // Errors are already handled by the slice, only notify about a successful result
      return;
    } finally {
      setIsUpdating(false);
    }
  }

  /** Ask the user to confirm changing the role for the whole team */
  async function confirmTeamRoleChange(role_1) {
    return createDialog({
      title: "Change Workspace Role",
      confirmText: "Change Role",
      isHideClose: true
    }, <Text variant="bodyM">
        Are you sure you want to change the role of{" "}
        <var>
          <strong>{teamName}</strong>{" "}
        </var>
        to <strong>{formatUserRoleType(role_1)}</strong>? This will affect all
        members of this team.
      </Text>);
  }
  return <Stack direction="row">
      <SelectCompanyRole selectedRole={memberIdentityRole.role} onChange={onRoleChange} variant={variant} menuProps={menuProps} roles={getAssignableWorkspaceRoles("change", canAssignManagers)} />
      {isUpdating && <FaroButtonSpinner size="14px" />}
      {member && roleAwaitingXgSeat && <XgSeatRequiredForRoleDialog member={member} role={roleAwaitingXgSeat} onClose={() => setRoleAwaitingXgSeat(undefined)} onSeatAssigned={() => applyRoleChange(roleAwaitingXgSeat)} />}
    </Stack>;
}